import initSqlJs, { type Database } from 'sql.js';
import sqlWasm from 'sql.js/dist/sql-wasm.wasm?url';
import { unzipSync } from 'fflate';
import { decode as decodePng } from 'fast-png';
import { idbGet, idbSet } from '../lib/idb';
import { manifestInfo } from '../lib/manifest';
import { reloadIfStale } from '../lib/stale';

/**
 * Builds a 3D "scene package" of equipped armour from Bungie's gear data for the three.js viewer (port of the app's
 * Gear3D.BuildAsync): Bungie's 3D gear database (SQLite, read with sql.js, kept in the browser once per Bungie update),
 * the geometry and texture containers from Bungie's gear CDN, the texture plates stitched together and the raw dye data.
 * The package is a set of in-memory files (name -> blob URL) the viewer page reads; the stitched plates go as raw
 * pixels (the viewer only reads their pixels back: encoding them as PNG took about a second a plate).
 */

export interface GearPiece { hash: number; name: string; bucket: number; shader?: number; shaderName: string; ornament?: number; ornamentName: string; weapon?: boolean }
export interface RawImage { width: number; height: number; data: Uint8ClampedArray }   // RGBA, opaque
export interface Scene { files: Record<string, string | RawImage>; pieces: number; total: number; triangles: number; textured: number; ornaments: string[]; notes: string[] }

const BUNGIE = 'https://www.bungie.net';
const signed = (h: number) => (h > 0x7fffffff ? h - 0x100000000 : h);

// ---------------- the gear database ----------------

let dbP: Promise<{ db: Database; cdn: Record<string, string> }> | undefined;
let dbStage: (s: string) => void = () => {};   // the latest caller's progress line

/** Opens the gear database ahead of time (the 3D views call it as they open, while the profile still loads). */
export const warmGear3d = () => { gearDb(() => {}).catch(() => { /* said again when the scene is built */ }); };

function gearDb(stage: (s: string) => void) {
  dbStage = stage;
  return (dbP ??= (async () => {
    const m = await manifestInfo();   // the manifest the site already asked for, not a second request
    const info = m.gear.dbs[0];
    if (!info || !m.gear.cdn) throw new Error('Bungie does not publish the 3D gear database any more.');
    let bytes: Uint8Array | undefined = (await idbGet<{ path: string; bytes: Uint8Array }>('g3d:db').then((k) => (k && k.path === info.path ? k.bytes : undefined)));
    if (!bytes) {
      dbStage("Downloading Bungie's 3D gear database (one time)...");
      const r = await fetch(BUNGIE + info.path);
      if (!r.ok) throw new Error(`Bungie's 3D gear database could not be downloaded (${r.status}).`);
      bytes = new Uint8Array(await r.arrayBuffer());
      void idbSet('g3d:db', { path: info.path, bytes });
    }
    if (bytes[0] === 0x50 && bytes[1] === 0x4b) bytes = Object.values(unzipSync(bytes))[0];
    const SQL = await initSqlJs({ locateFile: () => sqlWasm }).catch(async (e) => { await reloadIfStale(); throw e; });   // its file gone after a deploy: reload
    return { db: new SQL.Database(bytes), cdn: m.gear.cdn };
  })().catch((e) => { dbP = undefined; throw e; }));
}

function asset(db: Database, hash: number): any {
  for (const id of [hash, signed(hash)]) {
    const r = db.exec('SELECT json FROM DestinyGearAssetsDefinition WHERE id = ?', [id]);
    const v = r[0]?.values?.[0]?.[0];
    if (v !== undefined) return JSON.parse(typeof v === 'string' ? v : new TextDecoder().decode(v as Uint8Array));
  }
  return null;
}

// ---------------- files from Bungie's gear CDN (the browser's own cache keeps them) ----------------

const fileCache = new Map<string, Promise<Uint8Array | null>>();
function file(url: string) {
  let p = fileCache.get(url);
  if (!p) {
    p = fetch(url).then(async (r) => (r.ok ? new Uint8Array(await r.arrayBuffer()) : null)).catch(() => null);
    fileCache.set(url, p);
  }
  return p;
}

// ---------------- TGXM container ----------------

const ascii = (b: Uint8Array, start: number, end: number) => String.fromCharCode(...b.subarray(start, end));

export function readTgx(data: Uint8Array): Record<string, Uint8Array> {
  if (data.length < 16 || ascii(data, 0, 4) !== 'TGXM') throw new Error('not a TGXM file');
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const fileOffset = dv.getInt32(8, true), count = dv.getInt32(12, true);
  const files: Record<string, Uint8Array> = {};
  for (let f = 0; f < count; f++) {
    const h = fileOffset + 0x110 * f;
    let end = h;
    while (end < h + 0x100 && data[end] !== 0) end++;
    const off = dv.getInt32(h + 0x100, true), size = dv.getInt32(h + 0x108, true);
    files[ascii(data, h, end)] = data.slice(off, off + size);
  }
  return files;
}

function sizeOf(type: string) {
  const t = type.replace('_vertex_format_attribute_', '');
  const kind = t.replace(/\d+$/, ''), comps = Number(t.slice(kind.length)) || 1;
  return comps * (kind === 'float' || kind === 'int' || kind === 'uint' ? 4 : kind === 'short' || kind === 'ushort' ? 2 : 1);
}

/** One vertex attribute (first 3 components) from an interleaved buffer. */
function readAttr(buf: Uint8Array, stride: number, offset: number, type: string, normalized: boolean) {
  const t = type.replace('_vertex_format_attribute_', '');
  const kind = t.replace(/\d+$/, ''), comps = Number(t.slice(kind.length));
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const n = Math.floor(buf.length / stride);
  const out = new Float32Array(n * 3);
  for (let v = 0; v < n; v++) {
    const o = v * stride + offset;
    for (let c = 0; c < 3 && c < comps; c++) {
      let x = 0;
      switch (kind) {
        case 'float': x = dv.getFloat32(o + c * 4, true); break;
        case 'short': x = dv.getInt16(o + c * 2, true); if (normalized) x = Math.max(-1, x / 32767); break;
        case 'ushort': x = dv.getUint16(o + c * 2, true); if (normalized) x /= 65535; break;
        case 'byte': x = dv.getInt8(o + c); if (normalized) x = Math.max(-1, x / 127); break;
        case 'ubyte': x = buf[o + c]; if (normalized) x /= 255; break;
        case 'int': x = dv.getInt32(o + c * 4, true); if (normalized) x /= 2147483647; break;
        case 'uint': x = dv.getUint32(o + c * 4, true); if (normalized) x /= 4294967295; break;
      }
      out[v * 3 + c] = x;
    }
  }
  return out;
}

class Mesh {
  pos: number[] = []; nrm: number[] = []; uv: number[] = [];
  groups = new Map<number, number[]>();
  diffuse?: Img; normal?: Img; stack?: Img;
  get vertices() { return this.pos.length / 3; }
  get triangles() { let n = 0; for (const g of this.groups.values()) n += g.length / 3; return n; }
}
interface Img { w: number; h: number; px: Uint8ClampedArray }   // RGBA, straight alpha

function addVertices(m: Mesh, pos: Float32Array, nrm: Float32Array | null, uv: Float32Array | null, su: number, sv: number, ou: number, ov: number) {
  const n = pos.length / 3;
  for (let i = 0; i < pos.length; i++) m.pos.push(pos[i]);
  for (let i = 0; i < pos.length; i++) m.nrm.push(nrm && nrm.length === pos.length ? -nrm[i] : 0);
  for (let i = 0; i < n; i++) {
    if (uv && uv.length === pos.length) m.uv.push(uv[i * 3] * su + ou, uv[i * 3 + 1] * sv + ov);
    else m.uv.push(0, 0);
  }
}

/** Triangles (3) or a triangle strip (5) from the index buffer, into a dye group (gear_dye_change_color_index). */
function addTriangles(m: Mesh, group: number, ib: Uint8Array, valueSize: number, start: number, count: number, prim: number, baseVertex: number, vertexCount: number) {
  let list = m.groups.get(group);
  if (!list) m.groups.set(group, (list = []));
  const dv = new DataView(ib.buffer, ib.byteOffset, ib.byteLength);
  const at = (k: number) => (valueSize === 4 ? dv.getUint32(k * 4, true) : dv.getUint16(k * 2, true));
  const total = ib.length / valueSize;
  if (prim === 3) {
    for (let i = 0; i + 2 < count; i += 3) {
      const s = start + i;
      if (s + 2 >= total) break;
      const a = at(s), b = at(s + 1), c = at(s + 2);
      if (a >= vertexCount || b >= vertexCount || c >= vertexCount) continue;
      list.push(baseVertex + a, baseVertex + b, baseVertex + c);
    }
  } else {
    for (let i = 0; i + 2 < count; i++) {
      const s = start + i;
      if (s + 2 >= total) break;
      const a = at(s), b = at(s + 1), c = at(s + 2);
      if (a === b || b === c || a === c) continue;
      if (a >= vertexCount || b >= vertexCount || c >= vertexCount) continue;   // strip restart / bad index
      if ((i & 1) === 0) list.push(baseVertex + a, baseVertex + b, baseVertex + c);
      else list.push(baseVertex + c, baseVertex + b, baseVertex + a);
    }
  }
}

// ---------------- images ----------------

/** PNG decoded by hand (a canvas would spoil the colour under see-through pixels: the gearstack's alpha is the dye mask), JPEG by the browser. */
async function decodeImage(bytes: Uint8Array): Promise<Img | null> {
  try {
    if (bytes[0] === 0x89 && bytes[1] === 0x50) {
      const p = decodePng(bytes);
      const n = p.width * p.height, px = new Uint8ClampedArray(n * 4);
      const src = p.data as ArrayLike<number>, ch = p.channels, sh = p.depth === 16 ? 8 : 0;
      for (let i = 0; i < n; i++) {
        if (p.palette) {
          const e = p.palette[src[i]] ?? [0, 0, 0, 255];
          px[i * 4] = e[0]; px[i * 4 + 1] = e[1]; px[i * 4 + 2] = e[2]; px[i * 4 + 3] = e.length > 3 ? e[3]! : 255;
        } else if (ch >= 3) {
          px[i * 4] = src[i * ch] >> sh; px[i * 4 + 1] = src[i * ch + 1] >> sh; px[i * 4 + 2] = src[i * ch + 2] >> sh; px[i * 4 + 3] = ch === 4 ? src[i * ch + 3] >> sh : 255;
        } else {
          const g = src[i * ch] >> sh;
          px[i * 4] = px[i * 4 + 1] = px[i * 4 + 2] = g; px[i * 4 + 3] = ch === 2 ? src[i * ch + 1] >> sh : 255;
        }
      }
      return { w: p.width, h: p.height, px };
    }
    const bmp = await createImageBitmap(new Blob([bytes as BlobPart]));
    const c = new OffscreenCanvas(bmp.width, bmp.height);
    const g = c.getContext('2d')!;
    g.drawImage(bmp, 0, 0);
    return { w: bmp.width, h: bmp.height, px: g.getImageData(0, 0, bmp.width, bmp.height).data };
  } catch {
    return null;
  }
}

/** Stitches the small texture images onto one "plate", the way Bungie's renderer does. */
async function composePlate(plate: any, images: Record<string, Uint8Array>): Promise<Img | null> {
  const size = plate?.plate_size, placements: any[] = (plate?.texture_placements ?? []).filter(Boolean);
  if (!size || size.length < 2 || !placements.length || !Object.keys(images).length) return null;
  const w = size[0], h = size[1];
  if (w <= 0 || h <= 0) return null;
  const px = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < px.length; i += 4) { px[i] = px[i + 1] = px[i + 2] = 128; px[i + 3] = 255; }
  let placed = 0;
  for (const p of placements) {
    const bytes = images[p.texture_tag_name];
    const src = bytes ? await decodeImage(bytes) : null;
    if (!src) continue;
    const x0 = p.position_x, y0 = p.position_y;
    for (let y = 0; y < src.h; y++) {
      const ty = y0 + y;
      if (ty < 0 || ty >= h) continue;
      for (let x = 0; x < src.w; x++) {
        const tx = x0 + x;
        if (tx < 0 || tx >= w) continue;
        const si = (y * src.w + x) * 4, di = (ty * w + tx) * 4;
        px[di] = src.px[si]; px[di + 1] = src.px[si + 1]; px[di + 2] = src.px[si + 2]; px[di + 3] = src.px[si + 3];
      }
    }
    placed++;
  }
  return placed ? { w, h, px } : null;
}

// ---------------- the build ----------------

/** Each item's finished 3D pieces, by item, shader, ornament and body (kept for this visit). */
/** The weapon buckets (kinetic, energy, power): their dye colours are chosen differently (see viewer3d.html buildMaps). */
const WEAPONS = [1498876634, 2465295065, 953998645];

const builtPieces = new Map<string, { pieces: Mesh[]; metas: string; detail: Record<string, Uint8Array> }>();
// each piece's files for the viewer, made once
const pieceFiles = new WeakMap<Mesh, { vtx: Blob; idx: [number, Blob, number][]; maps: [string, RawImage][] }>();

export async function buildScene(items: GearPiece[], female: boolean, stage: (s: string) => void): Promise<Scene> {
  stage("Reading Bungie's 3D gear database...");
  const { db, cdn } = await gearDb(stage);
  stage('Finding your armour in the gear database...');
  const arr = (x: any): any[] => (Array.isArray(x) ? x : x == null ? [] : [x]);
  const gearJs = async (a: any) => {
    const f = arr(a?.gear)[0];
    if (!f || !cdn.Gear) return null;
    const b = await file(`${BUNGIE}${cdn.Gear}/${f}`);
    try { return b ? JSON.parse(new TextDecoder().decode(b)) : null; } catch { return null; }
  };
  const loadTextures = async (content: any, idx: number[], images: Record<string, Uint8Array>) => {
    await Promise.all(idx.map(async (ti) => {
      const t = arr(content?.textures)[ti];
      if (!t || !cdn.Texture) return;
      const b = await file(`${BUNGIE}${cdn.Texture}/${t}`);
      if (!b) return;
      if (b.length >= 4 && ascii(b, 0, 4) === 'TGXM') { try { for (const [k, v] of Object.entries(readTgx(b))) images[k.replace(/\.[a-z0-9]+$/, '')] = v; } catch { /* broken container */ } }
      else images[t.replace(/\.[^.]+$/, '')] = b;
    }));
  };
  const indexes = (sets: any[], what: string) => [...new Set(sets.flatMap((s) => arr(s?.[what])).filter((x) => x != null).map(Number))];

  const notes: string[] = [];
  // every item at once (the downloads overlap; one after another this took a few seconds an item), put back in order after
  let ready = 0;
  const progress = () => stage(`Loading your armour in 3D (${ready} of ${items.length} pieces ready)...`);
  progress();
  const built = await Promise.all(items.map(async (item) => {
    const ck = `${item.hash}.${item.shader ?? 0}.${item.ornament ?? 0}.${female ? 1 : 0}`;
    let hit = builtPieces.get(ck);
    if (!hit) {
      const made = await buildItem(item);
      if (made.pieces.length) builtPieces.set(ck, (hit = { pieces: made.pieces, metas: JSON.stringify(made.metas), detail: made.detail }));
    }
    ready++;
    progress();
    return hit;
  }));
  const pieces: Mesh[] = [];
  const pieceMeta: any[] = [];
  const detail: Record<string, Uint8Array> = {};
  let loaded = 0, textured = 0, tris = 0;
  for (const hit of built) {
    if (!hit) continue;
    for (const p of hit.pieces) { pieces.push(p); tris += p.triangles; if (p.diffuse) textured++; }
    pieceMeta.push(...JSON.parse(hit.metas));
    Object.assign(detail, hit.detail);
    loaded++;
  }
  if (!pieces.length) throw new Error('No 3D geometry could be read. ' + notes.join('; '));

  /** One item's 3D pieces, their dye data and the dyes' detail images. */
  async function buildItem(item: GearPiece) {
    const pieces: Mesh[] = [], metas: any[] = [], detail: Record<string, Uint8Array> = {};
    const none = { pieces, metas, detail };
    let a: any = null, usedOrnament = false;
    if (item.ornament) { a = asset(db, item.ornament); usedOrnament = !!a; }
    a ??= asset(db, item.hash);
    if (!a) { notes.push(`${item.name} has no 3D data`); return none; }

    // dyes: the item's defaults, the equipped shader on top, locked (exotic) dyes last
    const resolved = new Map<number, any>();
    const apply = (list: any) => arr(list).forEach((d, j) => { if (d) resolved.set(d.slot_type_index ?? j, d); });
    const gear = await gearJs(a);
    if (gear) apply(gear.default_dyes);
    const sa = item.shader ? asset(db, item.shader) : null;
    if (sa) { const sg = await gearJs(sa); if (sg) apply(sg.custom_dyes); }
    if (gear) apply(gear.locked_dyes);

    const contents = arr(a.content);
    const content = contents.find((c) => c?.platform === 'mobile') ?? contents[0];
    if (!content) { notes.push(`${item.name} has no geometry`); return none; }
    // armour has separate male / female sets; other gear uses region sets
    const sets: any[] = [];
    if (content.female_index_set && content.male_index_set) sets.push(female ? content.female_index_set : content.male_index_set);
    else if (content.region_index_sets) for (const [name, v] of Object.entries(content.region_index_sets)) if (name !== '21') sets.push(...arr(v));
    if (content.dye_index_set) sets.push(...arr(content.dye_index_set));

    // the 3D files start downloading now, beside the textures
    const geometry = indexes(sets, 'geometry').map((gi) => arr(content.geometry)[gi]).filter(Boolean).map((f) => file(`${BUNGIE}${cdn.Geometry}/${f}`));
    const images: Record<string, Uint8Array> = {};
    // with the equipped shader's own texture files: its detail maps (cloth weave, metal grain...)
    const sc = sa ? arr(sa.content)[0] : null;
    await Promise.all([loadTextures(content, indexes(sets, 'textures'), images), sc && loadTextures(sc, indexes(arr(sc.dye_index_set), 'textures'), images)]);

    for (const geo of geometry) {
      const gb = await geo;
      if (!gb) continue;
      let files: Record<string, Uint8Array>;
      try { files = readTgx(gb); } catch { continue; }
      const metaKey = Object.keys(files).find((k) => k.endsWith('.js'));
      if (!metaKey) continue;
      const meta = JSON.parse(new TextDecoder().decode(files[metaKey]));
      const piece = new Mesh();
      for (const rm of arr(meta?.render_model?.render_meshes)) {
        if (!rm) continue;
        const layout = arr(rm.stage_part_vertex_stream_layout_definitions)[0];
        let pos: Float32Array | null = null, nrm: Float32Array | null = null, uv: Float32Array | null = null;
        const vbs = arr(rm.vertex_buffers);
        for (let b = 0; b < vbs.length; b++) {
          const fmt = arr(layout?.formats)[b], buf = files[vbs[b]?.file_name];
          const stride = vbs[b]?.stride_byte_size ?? 0;
          if (!fmt || !buf || stride <= 0) continue;
          // Bungie's offsets assume a short4 position, but the position is float4 (16 bytes): an element never
          // starts before the previous one ends (the normal really sits at 16, not 8)
          let end = 0;
          for (const el of arr(fmt.elements)) {
            const type = el?.type ?? '';
            const offset = Math.max(el?.offset ?? 0, end);
            end = offset + sizeOf(type);
            if ((el?.semantic_index ?? 0) !== 0) continue;
            const read = () => readAttr(buf, stride, offset, type, el.normalized === true || el.normalized === 'true');
            if (el.semantic === '_tfx_vb_semantic_position' && !pos) pos = read();
            if (el.semantic === '_tfx_vb_semantic_normal' && !nrm) nrm = read();
            if (el.semantic === '_tfx_vb_semantic_texcoord' && !uv) uv = read();
          }
        }
        const ib = files[rm.index_buffer?.file_name];
        if (!pos || !ib) continue;
        const ts = rm.texcoord_scale, to = rm.texcoord_offset;
        const [su, sv] = ts?.length >= 2 ? ts : [1, 1], [ou, ov] = to?.length >= 2 ? to : [0, 0];
        const baseVertex = piece.vertices, vcount = (pos as Float32Array).length / 3;
        addVertices(piece, pos, nrm, uv, su, sv, ou, ov);
        const vsize = rm.index_buffer?.value_byte_size;
        const parts = arr(rm.stage_part_list).filter(Boolean);
        if (!parts.length || (vsize !== 2 && vsize !== 4)) continue;
        // every render stage (the Skullfort's face plate is in a later one); repeats in other stages are skipped
        let use = parts.filter((p) => (p.lod_category?.value ?? 0) <= 3);
        if (!use.length) use = parts;
        const seen = new Set<number>();
        for (const pt of use) {
          if (seen.has(pt.start_index)) continue;
          seen.add(pt.start_index);
          const glow = ((pt.flags ?? 0) & 0x10) !== 0 ? 256 : 0;   // the glow layer (the Skullfort's light grille)
          addTriangles(piece, (pt.gear_dye_change_color_index ?? 0) + glow, ib, vsize, pt.start_index, pt.index_count, pt.primitive_type, baseVertex, vcount);
        }
      }
      if (piece.triangles === 0) continue;
      const plate = arr(meta?.texture_plates).find(Boolean);
      piece.diffuse = (await composePlate(plate?.plate_set?.diffuse, images)) ?? undefined;
      if (piece.diffuse) {
        piece.normal = (await composePlate(plate?.plate_set?.normal, images)) ?? undefined;
        piece.stack = (await composePlate(plate?.plate_set?.gearstack, images)) ?? undefined;
      }
      pieces.push(piece);
      // everything the viewer needs about this piece's dyes (raw Bungie values, per slot)
      const dyes: Record<string, any> = {};
      for (const [k, dye] of [...resolved].sort((x, y) => x[0] - y[0])) {
        const tx: Record<string, any> = {};
        for (const [tname, tv] of Object.entries<any>(dye.textures ?? {})) {
          const nm = String(tv?.name ?? '').replace(/\.[a-z0-9]+$/, '');
          const bt = nm ? images[nm] : undefined;
          if (!bt) continue;
          const ext = bt.length > 3 && bt[0] === 0x89 ? 'png' : bt.length > 3 && bt[0] === 0xff ? 'jpg' : null;
          if (!ext) continue;
          const fn = `det_${nm}.${ext}`;
          detail[fn] = bt;
          tx[tname] = { name: nm, file: fn };
        }
        dyes[k] = { material_properties: dye.material_properties, slot: k, textures: tx };
      }
      metas.push({ item: item.name, hash: String(item.hash), bucket: String(item.bucket), weapon: !!item.weapon || WEAPONS.includes(item.bucket), shader: item.shaderName, ornament: usedOrnament ? item.ornamentName : '', dyes });
    }
    // kept (builtPieces) for the next outfit that wears it: a swap then builds only the piece that changed
    return { pieces, metas, detail };
  }

  // the scene package: geometry, the maps (diffuse / normal / gearstack / dye mask) and the dye data, as in-memory files
  stage('Preparing the materials...');
  const out: Record<string, Blob> = {}, raw: Record<string, RawImage> = {};
  const sceneParts: any[] = [];
  for (let k = 0; k < pieces.length; k++) {
    const p = pieces[k];
    let made = pieceFiles.get(p);
    if (!made) {
      const v = new Float32Array(p.vertices * 8);
      for (let j = 0; j < p.vertices; j++) {
        v.set([p.pos[j * 3], p.pos[j * 3 + 1], p.pos[j * 3 + 2], p.nrm[j * 3] ?? 0, p.nrm[j * 3 + 1] ?? 0, p.nrm[j * 3 + 2] ?? 0, p.uv[j * 2] ?? 0, p.uv[j * 2 + 1] ?? 0], j * 8);
      }
      const idx: [number, Blob, number][] = [];
      for (const [g, list] of p.groups) if (list.length) idx.push([g, new Blob([new Uint32Array(list)]), list.length]);
      // the gearstack's alpha (the dye mask) goes in its own grey image; the stack itself is saved opaque
      // (into copies: the piece itself is kept for the next outfit)
      let stack = p.stack, mask: Img | undefined;
      if (stack) {
        const st = stack, opaque = new Uint8ClampedArray(st.px), m = new Uint8ClampedArray(st.px.length);
        for (let j = 0; j < opaque.length; j += 4) { m[j] = m[j + 1] = m[j + 2] = st.px[j + 3]; m[j + 3] = 255; opaque[j + 3] = 255; }
        stack = { ...st, px: opaque };
        mask = { w: st.w, h: st.h, px: m };
      }
      const maps: [string, RawImage][] = [];
      for (const [map, img] of [['diffuse', p.diffuse], ['normal', p.normal], ['stack', stack], ['dyemask', mask]] as [string, Img | undefined][])
        if (img) maps.push([map, { width: img.w, height: img.h, data: img.px }]);
      made = { vtx: new Blob([v]), idx, maps };
      pieceFiles.set(p, made);
    }
    out[`p${k}.vtx`] = made.vtx;
    for (const [g, blob, count] of made.idx) {
      out[`p${k}_g${g}.idx`] = blob;
      sceneParts.push({ piece: k, group: g, file: `p${k}_g${g}.idx`, count });
    }
    const pm = pieceMeta[k];
    pm.vertices = `p${k}.vtx`;
    pm.vertexCount = p.vertices;
    for (const [map, img] of made.maps) { raw[`p${k}_${map}`] = img; pm[map] = `p${k}_${map}`; }
  }
  for (const [fn, b] of Object.entries(detail)) out[fn] = new Blob([b as BlobPart], { type: fn.endsWith('png') ? 'image/png' : 'image/jpeg' });
  out['scene.json'] = new Blob([JSON.stringify({ version: 1, character: 'web', female, pieces: pieceMeta, parts: sceneParts, notes })], { type: 'application/json' });
  const files = { ...Object.fromEntries(Object.entries(out).map(([n, b]) => [n, URL.createObjectURL(b)])), ...raw };
  return { files, pieces: loaded, total: items.length, triangles: tris, textured, ornaments: pieceMeta.map((m) => m.ornament).filter(Boolean), notes };
}

export const freeScene = (s?: Scene) => { if (s) for (const u of Object.values(s.files)) if (typeof u === 'string') URL.revokeObjectURL(u); };
